import anime, { AnimeParams, AnimeInstance } from 'animejs';

// Common interactive element styles that prevent unwanted mobile behaviors
export const interactiveStyle = {
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  WebkitTouchCallout: 'none',
  WebkitUserSelect: 'none',
  userSelect: 'none',
  outline: 'none',
} as const;

// Animation timing presets
export const timings = {
  fast: 400,
  medium: 600,
  slow: 800,
} as const;

// Common easing patterns
export const easings = {
  bounce: 'easeInOutElastic(1, .5)',
  smooth: 'easeInOutQuad',
  springy: 'easeInOutBack',
} as const;

// Type for supported event triggers
export type TriggerEvent = 'hover' | 'click' | 'touch';

interface ContinuousAnimation {
  instance: anime.AnimeInstance;
  element: Element;
}

interface AnimationManager {
  continuous: Map<Element, ContinuousAnimation>;
  interactive: Map<Element, anime.AnimeInstance>;
}

const animationManager: AnimationManager = {
  continuous: new Map(),
  interactive: new Map(),
};

// Helper to create and manage continuous animations
export const createContinuousAnimation = (
  element: Element | null,
  params: AnimeParams
): (() => void) => {
  if (!element) return () => {};

  const instance = anime({
    ...params,
    targets: element,
    loop: true,
    autoplay: true,
  });

  animationManager.continuous.set(element, {
    instance,
    element,
  });

  return () => {
    instance.pause();
    animationManager.continuous.delete(element);
  };
};

// Helper to create a floating animation
export const createFloatingAnimation = (
  element: Element | null,
  delay: number = 0
): (() => void) => {
  return createContinuousAnimation(element, {
    translateY: [-2, 2],
    rotate: [-1, 1],
    duration: 2000,
    direction: 'alternate',
    easing: easings.smooth,
    delay,
  });
};

// Reusable animation configurations with continuous animation handling
export const animations = {
  rotate360: (target: Element): AnimeParams => ({
    targets: target,
    rotate: [0, 360, 0],
    scale: [1, 1.2, 1],
    duration: timings.slow,
    easing: easings.springy,
    autoplay: false,
  }),

  gentleBounce: (target: Element): AnimeParams => ({
    targets: target,
    translateY: [0, -2, 0],
    duration: timings.fast,
    easing: easings.bounce,
    autoplay: false,
  }),

  rock: (target: Element | Element[] | NodeListOf<Element>): AnimeParams => ({
    targets: target,
    rotate: [0, 15, -15, 0],
    duration: timings.medium,
    easing: easings.smooth,
    delay: anime.stagger(100),
    autoplay: false,
  }),

  fadeIn: (target: Element): AnimeParams => ({
    targets: target,
    opacity: [0, 1],
    translateY: [20, 0],
    duration: timings.medium,
    easing: easings.smooth,
    autoplay: false,
  }),

  fadeOut: (target: Element): AnimeParams => ({
    targets: target,
    opacity: [1, 0],
    translateY: [0, 20],
    duration: timings.medium,
    easing: easings.smooth,
    autoplay: false,
  }),

  slideIn: (target: Element, direction: 'left' | 'right' = 'right'): AnimeParams => ({
    targets: target,
    opacity: [0, 1],
    translateX: [direction === 'left' ? -50 : 50, 0],
    duration: timings.medium,
    easing: easings.smooth,
    autoplay: false,
  }),

  slideOut: (target: Element, direction: 'left' | 'right' = 'right'): AnimeParams => ({
    targets: target,
    opacity: [1, 0],
    translateX: [0, direction === 'left' ? -50 : 50],
    duration: timings.medium,
    easing: easings.smooth,
    autoplay: false,
  }),

  scaleIn: (target: Element): AnimeParams => ({
    targets: target,
    opacity: [0, 1],
    scale: [0.8, 1],
    duration: timings.medium,
    easing: easings.springy,
    autoplay: false,
  }),

  scaleOut: (target: Element): AnimeParams => ({
    targets: target,
    opacity: [1, 0],
    scale: [1, 0.8],
    duration: timings.medium,
    easing: easings.springy,
    autoplay: false,
  }),
} as const;

// Helper to attach animations with continuous animation handling
export const attachAnimation = (
  element: HTMLElement | null,
  animation: AnimeParams,
  triggers: TriggerEvent[] = ['hover', 'touch']
) => {
  if (!element) return;

  const instance = anime(animation);
  animationManager.interactive.set(element, instance);

  const playAnimation = (e?: Event) => {
    if (e) e.preventDefault();

    // Pause continuous animation if it exists
    const continuous = animationManager.continuous.get(element);
    if (continuous) {
      continuous.instance.pause();
    }

    instance.restart();
  };

  const onAnimationComplete = () => {
    // Resume continuous animation if it exists
    const continuous = animationManager.continuous.get(element);
    if (continuous) {
      continuous.instance.play();
    }
  };

  // Add completion callback
  if (animation.complete) {
    const originalComplete = animation.complete;
    instance.complete = function (anim: AnimeInstance) {
      originalComplete.call(this, anim);
      onAnimationComplete();
    };
  } else {
    instance.complete = onAnimationComplete;
  }

  // Attach specified triggers
  triggers.forEach(trigger => {
    switch (trigger) {
      case 'hover':
        element.addEventListener('mouseenter', playAnimation);
        break;
      case 'click':
        element.addEventListener('click', playAnimation);
        break;
      case 'touch':
        element.addEventListener('touchstart', playAnimation);
        break;
    }
  });

  // Return cleanup function
  return () => {
    triggers.forEach(trigger => {
      switch (trigger) {
        case 'hover':
          element.removeEventListener('mouseenter', playAnimation);
          break;
        case 'click':
          element.removeEventListener('click', playAnimation);
          break;
        case 'touch':
          element.removeEventListener('touchstart', playAnimation);
          break;
      }
    });
    instance.pause();
    animationManager.interactive.delete(element);
  };
};

// Helper to attach multiple animations
export const attachAnimations = (
  configs: {
    element: HTMLElement | null;
    animation: AnimeParams;
    triggers: TriggerEvent[];
  }[]
) => {
  const cleanupFns = configs.map(({ element, animation, triggers }) =>
    attachAnimation(element, animation, triggers)
  );

  return () => cleanupFns.forEach(fn => fn?.());
};

// Helper to create a staggered entrance animation for multiple elements
export const createStaggeredEntrance = (
  elements: Element[] | NodeListOf<Element>,
  animation: 'fade' | 'slide' | 'scale' = 'fade',
  options: {
    staggerDelay?: number;
    direction?: 'left' | 'right';
    duration?: number;
  } = {}
): (() => void) => {
  const { staggerDelay = 100, direction = 'right', duration = timings.medium } = options;

  let baseAnimation: AnimeParams;
  switch (animation) {
    case 'slide':
      baseAnimation = {
        opacity: [0, 1],
        translateX: [direction === 'left' ? -50 : 50, 0],
      };
      break;
    case 'scale':
      baseAnimation = {
        opacity: [0, 1],
        scale: [0.8, 1],
      };
      break;
    default: // fade
      baseAnimation = {
        opacity: [0, 1],
        translateY: [20, 0],
      };
  }

  const instance = anime({
    targets: elements,
    ...baseAnimation,
    duration,
    delay: anime.stagger(staggerDelay),
    easing: easings.smooth,
    autoplay: true,
  });

  return () => {
    instance.pause();
  };
};

// Example usage:
/*
import { useEffect, useRef } from 'react';
import { animations, attachAnimations, interactiveStyle } from './interactions';

const MyComponent = () => {
  const elementRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const cleanup = attachAnimations([
      {
        element: elementRef.current,
        animation: animations.rotate360(elementRef.current!),
        triggers: ['hover', 'touch']
      }
    ]);
    
    return cleanup;
  }, []);
  
  return <div ref={elementRef} style={interactiveStyle}>Interactive Element</div>;
};
*/
